import { AppChannel, Channel } from "@/config/channel"
import {
  computeErrorType,
  computeHasOther,
  computePostLoginAction,
} from "@/lib/post-login-actions"
import { safeWindow } from "@/lib/safe-window"
import { seapServiceControl } from "@/lib/seap"
import useGlobalParamsStore from "@/store/global-params-store"
import { Err, isOk, Ok, Result } from "@pacote/result"
import useSWRMutation from "swr/mutation"
import { v4 as uuidV4 } from "uuid"
import { z } from "zod"
import { ApiFailure, UnhandledApiError } from "./api-config"
import { baseClientFetcher } from "./base-fetchers"
import { InfoMessageList, messagesFetcher } from "./get-messages"
import {
  ComputedLoginFields,
  LoginErrorResponse,
  TloginErrorResponse,
} from "./post-login-sso"
import { ssoLoginV3ResponseSchema } from "./post-login-sso-v3"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

/**
 * Device information for trusted device registration
 */
type DeviceInfo = {
  deviceId?: string
  pushId?: string
  deviceName?: string
  deviceDescription?: string
}

/**
 * Payload for confirming MFA login with OTP
 */
type ConfirmLoginPayload = {
  loginPendingID: number
  mfaMethod: "sms" | "email" | "push"
  storeDevice?: boolean
  device?: DeviceInfo
  channel: Channel
  username: string
  password: string
}

/**
 * Response schema for confirm login endpoint
 * Similar to regular login response but specific to MFA confirmation
 */
const confirmLoginResponseSchema = z.object({
  ...ssoLoginV3ResponseSchema.shape,
  expire: z.string().optional(),
  deviceStored: z.boolean().optional(),
})

type ConfirmLoginResponse = z.infer<typeof confirmLoginResponseSchema>

type TConfirmLoginResponse = ConfirmLoginResponse & ComputedLoginFields

type ConfirmLoginResult = Result<TConfirmLoginResponse, TloginErrorResponse>

/* -------------------------------------------------------------------------------------------------
 * service endpoint
 * -----------------------------------------------------------------------------------------------*/

const confirmLoginServiceEndpoint = "/authentication-ms/v3/sso/login/confirm"

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const handleSuccessfulLogin = async (
  loginResponse: ConfirmLoginResponse,
  sessionId: string,
  arg: ConfirmLoginPayload,
): Promise<TConfirmLoginResponse> => {
  // Update session ID if needed
  if (!sessionStorage.getItem("session-id")) {
    sessionStorage.setItem("session-id", sessionId)
  }

  const { jwt, status, billCodeId } = loginResponse

  let messages: InfoMessageList = []

  if (jwt) {
    useGlobalParamsStore.getState().setJwt(jwt)
    const messagesServiceResult = await messagesFetcher()

    if (isOk(messagesServiceResult)) {
      messages = messagesServiceResult.value.infoMessageList
    }
  }

  // Handle SEAP service control for Sisal
  if (
    status === "SUCCESS" &&
    arg.channel === AppChannel.PortaleWebFullResponsiveWwwSisalIt &&
    billCodeId != null
  ) {
    await seapServiceControl(billCodeId)
  }

  const postLoginAction = computePostLoginAction(
    loginResponse,
    arg.channel,
    messages,
  )

  const hasOther = computeHasOther<ConfirmLoginResponse>(
    loginResponse,
    messages,
  )

  return {
    ...loginResponse,
    postLoginAction,
    hasOther,
    messages,
  }
}

/**
 * Fetcher for confirming MFA login with OTP
 * Handles OTP verification and optional device trust registration
 */
const confirmLoginFetcher = async (
  key: string,
  { arg }: { arg: ConfirmLoginPayload },
): Promise<ConfirmLoginResult> => {
  const sessionId = sessionStorage.getItem("session-id") || uuidV4()

  const deviceInfo = getDeviceInfo(arg)

  const loginServiceResult = await baseClientFetcher<
    ConfirmLoginResponse,
    LoginErrorResponse
  >(
    {
      url: key,
      method: "POST",
      headers: {
        "X-Session-Id": sessionId,
        ...(safeWindow()?.APP_VENDOR === "sisal"
          ? { "x-set-queue": "true" }
          : {}),
      },
      body: {
        loginPendingID: arg.loginPendingID,
        mfaMethod: arg.mfaMethod,
        storeDevice: arg.storeDevice ?? false,
        ...(deviceInfo && { device: deviceInfo }),
        username: arg.username,
        password: arg.password,
      },
      successSchemaValidator: confirmLoginResponseSchema,
      throwOnValidationError: true,
    },
    { enrichRequestData: false },
  )

  if (isOk(loginServiceResult)) {
    const result = await handleSuccessfulLogin(
      loginServiceResult.value,
      sessionId,
      arg,
    )
    return Ok(result)
  }

  // Handle error response
  if (
    loginServiceResult.value.status !== 422 &&
    loginServiceResult.value.status !== 417
  ) {
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 or 417 httpStatus was returned on request: ${confirmLoginServiceEndpoint}`,
    })
  }

  const errorResponse = loginServiceResult.value.data

  const errorType = computeErrorType(loginServiceResult.value)

  const counter = errorResponse.error?.errorInfos?.counter

  return Err({
    type: errorType,
    counter: 4,
    errorResponse,
    ...(Array.isArray(counter) && { counter: Number(counter[0]) + 1 }),
  })
}

function getDeviceInfo(
  confirmLoginPayload: ConfirmLoginPayload,
): DeviceInfo | null {
  if (!confirmLoginPayload.storeDevice) {
    return null
  }

  const deviceId = confirmLoginPayload.device?.deviceId

  if (!deviceId) {
    console.warn("Cannot store device: deviceId not available")
    return null
  }

  return {
    deviceId,
    pushId: confirmLoginPayload.device?.pushId,
    deviceName: confirmLoginPayload.device?.deviceName,
    deviceDescription: confirmLoginPayload.device?.deviceDescription,
  }
}

/* -------------------------------------------------------------------------------------------------
 * hook
 * -----------------------------------------------------------------------------------------------*/

/**
 * SWR mutation hook for confirming MFA login
 * Used after OTP generation to verify the OTP code
 */
const useConfirmLoginMutation = () =>
  useSWRMutation(confirmLoginServiceEndpoint, confirmLoginFetcher, {
    populateCache: true,
  })

/* -------------------------------------------------------------------------------------------------
 * exports
 * -----------------------------------------------------------------------------------------------*/

export { confirmLoginServiceEndpoint, useConfirmLoginMutation }
export type {
  ConfirmLoginPayload,
  ConfirmLoginResult,
  DeviceInfo,
  TConfirmLoginResponse,
}
